/**
 * The words a fan reads for a "YYYY-MM-DD" day (Scores UX spec v1.0 section
 * 0.4). Shared by web and mobile.
 *
 * String tables only, on purpose: no Intl and no toLocale anything. A day
 * string from events-api is already in the fan's zone, and formatting it
 * through Intl again both risks a one day shift and renders differently under
 * macOS and Linux ICU. The weekday comes from UTC calendar arithmetic, which
 * is the same everywhere.
 *
 * Month abbreviations follow the house style the apps already use, so
 * September is "Sept", never "Sep". Ranges use an ASCII hyphen ("Oct 16-20"),
 * never an en dash.
 */

export const WEEKDAY_SHORT = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

export const WEEKDAY_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** What a day chip reads on its first line for today. */
export const TODAY_LABEL = "Today";

type typeDayParts = { year: number; month: number; dayOfMonth: number };

function parseDay(day: string): typeDayParts {
  const [yearPart, monthPart, dayPart] = day.split("-");
  return {
    year: Number(yearPart),
    month: Number(monthPart),
    dayOfMonth: Number(dayPart),
  };
}

function weekdayIndex(day: string): number {
  const { year, month, dayOfMonth: dom } = parseDay(day);
  return new Date(Date.UTC(year, month - 1, dom)).getUTCDay();
}

/** "Mon" */
export function weekdayShort(day: string): string {
  return WEEKDAY_SHORT[weekdayIndex(day)] ?? "";
}

/** "Monday" */
export function weekdayLong(day: string): string {
  return WEEKDAY_LONG[weekdayIndex(day)] ?? "";
}

/** 19 */
export function dayOfMonth(day: string): number {
  return parseDay(day).dayOfMonth;
}

/** "Oct" ("Sept" for September). */
export function monthShort(day: string): string {
  return MONTH_SHORT[parseDay(day).month - 1] ?? "";
}

/** True when the two day strings name the same month of the same year. */
export function isSameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

export function isToday(day: string, todayDay: string): boolean {
  return day === todayDay;
}

/**
 * "Monday, Oct 19". With `todayDay`, a day in another year than today gains
 * the year: "Monday, Oct 19, 2025". The heading, the empty line and every
 * accessible day name use this; a raw ISO day is never shown or read aloud.
 */
export function longDayLabel(day: string, todayDay?: string): string {
  const { year, dayOfMonth: dom } = parseDay(day);
  const base = `${weekdayLong(day)}, ${monthShort(day)} ${String(dom)}`;
  if (todayDay === undefined || todayDay === "") return base;
  return parseDay(todayDay).year === year ? base : `${base}, ${String(year)}`;
}

/** "Thu, Oct 22", for a compact button such as "Go to Thu, Oct 22". */
export function shortDayLabel(day: string): string {
  return `${weekdayShort(day)}, ${monthShort(day)} ${String(dayOfMonth(day))}`;
}

export type typeWeekRange = {
  seasonWeek: number;
  /** "YYYY-MM-DD", inclusive. */
  firstDay: string;
  lastDay: string;
};

/** "Oct 16-20", "Oct 30-Nov 3", or "Oct 16" for a one day week. */
export function weekRangeShortLabel(week: typeWeekRange): string {
  const first = `${monthShort(week.firstDay)} ${String(dayOfMonth(week.firstDay))}`;
  if (week.firstDay === week.lastDay) return first;
  const lastDom = String(dayOfMonth(week.lastDay));
  const last = isSameMonth(week.firstDay, week.lastDay)
    ? lastDom
    : `${monthShort(week.lastDay)} ${lastDom}`;
  return `${first}-${last}`;
}

/** The two lines of a week chip: "Wk 7" over "Oct 16-20". */
export function weekChipLabel(week: typeWeekRange): {
  week: string;
  range: string;
} {
  return {
    week: `Wk ${String(week.seasonWeek)}`,
    range: weekRangeShortLabel(week),
  };
}

/** "Week 7, Oct 16-20", the league view heading in week mode. */
export function weekHeadingLabel(week: typeWeekRange): string {
  return `Week ${String(week.seasonWeek)}, ${weekRangeShortLabel(week)}`;
}

/**
 * Line 1 of a day chip: "Today" for today, the month ("Nov") when the month
 * changes from the chip before it, otherwise the weekday ("Mon").
 */
export function dayChipTopLabel(input: {
  day: string;
  todayDay: string;
  previousDay: string | null;
}): string {
  if (isToday(input.day, input.todayDay)) return TODAY_LABEL;
  if (
    input.previousDay !== null &&
    !isSameMonth(input.previousDay, input.day)
  ) {
    return monthShort(input.day);
  }
  return weekdayShort(input.day);
}
